import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { IconName, IconSize } from '@databox/icons/dist/types';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

export interface Args {
	feature?: BillingFeature;
	text?: string;
	icon?: IconName;
	iconSize?: IconSize;
	trackingOrigin?: string;
	onLimitationsHandled?: () => void;
}

export default abstract class CommonUpgradePlanBaseComponent<T extends Args> extends Component<T> {
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;

	get text() {
		const { text } = this.args;
		if (text) {
			return text;
		} else if (this.billingService.isCanceled) {
			return 'Resubscribe';
		} else if (this.billingService.isExpiredTrial) {
			return 'Subscribe';
		} else {
			return 'Unlock';
		}
	}

	get targetPlan() {
		const { feature } = this.args;
		return feature
			? this.billingService.getNextPlanForFeatureWithSameBillingCycle(feature)
			: null;
	}

	get isReportsAdvanced() {
		return this.args.feature === BillingFeature.ADVANCED_REPORTS;
	}

	@action
	handleLimitations() {
		const { feature } = this.args;

		if (feature) {
			this.billingService.handleLimitations(feature);
		} else {
			if (this.billingService.checkForPermissions(feature)) {
				const { onLimitationsHandled, trackingOrigin } = this.args,
					origin = trackingOrigin || feature,
					type = this.billingService.getTrackingSubscriptionType(),
					targetPlan = this.targetPlan;

				this.analyticsService.event(TrackingEvent.UPGRADE_INITIATED, {
					origin: origin,
					type,
				});
				if (this.isReportsAdvanced) {
					this.billingService.transitionToPlansAndShowReportsGrowthBubble();
				} else if (targetPlan) {
					this.billingService.switchPlan(targetPlan, {
						billingPeriodChangePopup:
							origin as TrackingOriginForSubscriptionChangePopup,
					});
				} else {
					this.navigationService.transitionToRoute('settings.billing.plans');
				}

				onLimitationsHandled?.();
			}
		}
	}
}
